import { sortBy } from 'lodash';
import { put, call, select } from 'redux-saga/effects';
import * as types from './company-tools-category.types';
import * as actions from './company-tools-category.actions';
import { companyToolsCategorySelectors } from './company-tools-category.selectors';
import * as api from '../../api/request-config/company-tools-category.api';
import { apiRequest } from '../../api/api.saga-services';
import * as companyContextsResourceService from '../company-contexts/company-contexts.saga-service';

export function* storeCompanyToolsCategory(
  companyToolsCategory: types.ICompanyToolsCategory | types.ICompanyToolsCategory[],
) {
  yield put(actions.storeCompanyToolsCategory(companyToolsCategory));

  return companyToolsCategory;
}

export function* clearCompanyToolsCategory(companyToolsCategoryId: types.ICompanyToolsCategory['id']) {
  yield put(actions.clearCompanyToolsCategory(companyToolsCategoryId));
}

export function* clearAllCompanyToolsCategories() {
  yield put(actions.clearCompanyToolsCategory(null));
}

export function* searchCompanyToolsCategories() {
  const rawCompanyToolsCategories = (yield call(apiRequest, api.searchCompanyToolsCategoriesApi())) as
    | Record<string, unknown>[]
    | null;
  if (rawCompanyToolsCategories === null) {
    return [];
  }

  yield call(clearAllCompanyToolsCategories);
  const companyToolsCategories = rawCompanyToolsCategories.map((r) => normalizeCompanyToolsCategory(r));
  yield call(storeCompanyToolsCategory, companyToolsCategories);

  const nestedContexts = rawCompanyToolsCategories.flatMap((r) => extractNestedCompanyContexts(r));
  if (rawCompanyToolsCategories.some((r) => 'companyContexts' in r || 'company_contexts' in r)) {
    yield call(companyContextsResourceService.storeCompanyContextsFromRawList, nestedContexts);
  }

  return companyToolsCategories.map((companyToolsCategory) => companyToolsCategory.id);
}

export function* createCompanyToolsCategory(data: types.ICompanyToolsCategoryCreate) {
  const rawCompanyToolsCategory = (yield call(apiRequest, api.createCompanyToolsCategoryApi(data))) as Record<
    string,
    unknown
  > | null;
  if (!rawCompanyToolsCategory) {
    return;
  }
  const companyToolsCategory = normalizeCompanyToolsCategory(rawCompanyToolsCategory);
  yield call(storeCompanyToolsCategory, companyToolsCategory);
  return companyToolsCategory;
}

export function* updateCompanyToolsCategory(data: types.ICompanyToolsCategoryUpdate) {
  const raw = (yield call(apiRequest, api.updateCompanyToolsCategoryApi(data))) as Record<string, unknown> | null;
  if (!raw) {
    return;
  }
  const existing = (yield select(companyToolsCategorySelectors.selectResourceById, String(raw.id))) as
    | types.ICompanyToolsCategory
    | undefined;
  const companyToolsCategory: types.ICompanyToolsCategory = {
    ...normalizeCompanyToolsCategory(raw),
    companyContextIds: existing?.companyContextIds ?? [],
  };

  yield call(storeCompanyToolsCategory, companyToolsCategory);
  return companyToolsCategory;
}

export function* deleteCompanyToolsCategory(companyToolsCategoryId: types.ICompanyToolsCategory['id']) {
  const result = yield call(apiRequest, api.deleteCompanyToolsCategoryApi(companyToolsCategoryId));
  if (result !== null) {
    yield call(clearCompanyToolsCategory, companyToolsCategoryId);
    return true;
  }
  return false;
}

function extractNestedCompanyContexts(raw: Record<string, unknown>): Record<string, unknown>[] {
  const nested = raw.companyContexts ?? raw.company_contexts;
  return Array.isArray(nested) ? (nested as Record<string, unknown>[]) : [];
}

function extractCompanyContextIds(raw: Record<string, unknown>): types.ICompanyToolsCategory['companyContextIds'] {
  return sortBy(extractNestedCompanyContexts(raw), (context) => {
    const sort = context.sort;
    return typeof sort === 'number' ? sort : Number(sort) || 0;
  }).map((context) => String(context.id));
}

function normalizeCompanyToolsCategory(raw: Record<string, unknown>): types.ICompanyToolsCategory {
  return {
    id: String(raw.id),
    title: typeof raw.title === 'string' ? raw.title : '',
    color: typeof raw.color === 'string' ? raw.color : '',
    avatar: typeof raw.avatar === 'string' && raw.avatar !== '' ? raw.avatar : null,
    description: typeof raw.description === 'string' ? raw.description : '',
    companyContextIds: extractCompanyContextIds(raw),
    createdAt: String(raw.createdAt ?? raw.created_at ?? ''),
    updatedAt: String(raw.updatedAt ?? raw.updated_at ?? ''),
  };
}
